import { useState } from 'react';

export default function ProductShowcase() {
  const [activeTab, setActiveTab] = useState('playground');

  const tabs = [
    {
      id: 'playground',
      name: 'AI Content Playground',
      icon: 'ri-ai-generate',
      description: 'Create engaging content with AI assistance',
    },
    {
      id: 'analytics',
      name: 'Smart Analytics',
      icon: 'ri-line-chart-line',
      description: 'Track performance with intelligent insights',
    },
    {
      id: 'planning',
      name: 'Post Planning',
      icon: 'ri-calendar-event-line',
      description: 'Schedule and organize your content calendar',
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-white to-[#e4f4ff]/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center px-4 py-2 bg-[#e4f4ff]/30 rounded-full text-[#22577A] font-medium mb-6">
            <i className="ri-eye-line mr-2"></i>
            See It In Action
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-[#22577A] mb-6">
            Powerful Features,
            <span className="block text-[#38a3a5]">Beautiful Experience</span>
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Explore the tools that make AgenticAI the smartest way to manage your social media
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-col sm:flex-row justify-center gap-4 mb-12">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-4 rounded-xl font-semibold transition-all duration-300 flex items-center gap-3 ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white shadow-xl scale-105'
                  : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-[#66b3ff] hover:shadow-lg'
              }`}
            >
              <i className={`${tab.icon} text-2xl`}></i>
              <div className="text-left">
                <div className="font-bold">{tab.name}</div>
                <div
                  className={`text-xs ${
                    activeTab === tab.id ? 'text-white/80' : 'text-gray-500'
                  }`}
                >
                  {tab.description}
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="relative">
          {/* AI Content Playground */}
          {activeTab === 'playground' && (
            <div className="animate-fadeIn">
              <div className="bg-white rounded-3xl shadow-2xl border-2 border-gray-200 overflow-hidden">
                <div className="grid lg:grid-cols-5 gap-0">
                  {/* Left Sidebar - Content Types */}
                  <div className="lg:col-span-1 bg-[#f8f9fa] border-r border-gray-200 p-6">
                    <h3 className="text-sm font-bold text-gray-500 uppercase mb-4">
                      Content Types
                    </h3>
                    <div className="space-y-2">
                      {[
                        { icon: 'ri-facebook-fill', name: 'Facebook Post', color: '#1877F2' },
                        { icon: 'ri-instagram-line', name: 'Instagram Caption', color: '#E4405F' },
                        { icon: 'ri-linkedin-fill', name: 'LinkedIn Article', color: '#0A66C2' },
                        { icon: 'ri-twitter-x-line', name: 'Tweet Thread', color: '#000000' },
                        { icon: 'ri-tiktok-line', name: 'TikTok Script', color: '#000000' },
                      ].map((type, idx) => (
                        <div
                          key={idx}
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                            idx === 0
                              ? 'bg-white shadow-sm border-2 border-[#66b3ff]'
                              : 'hover:bg-white hover:shadow-sm'
                          }`}
                        >
                          <i className={type.icon} style={{ color: type.color }}></i>
                          <span className="text-sm font-medium text-gray-700">{type.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Main Content Area */}
                  <div className="lg:col-span-4 p-8">
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-2xl font-bold text-[#22577A]">AI Content Generator</h3>
                        <div className="flex gap-2">
                          <button className="px-4 py-2 bg-[#e4f4ff]/30 text-[#22577A] rounded-lg text-sm font-semibold hover:bg-[#e4f4ff]/50 transition-colors">
                            Save Draft
                          </button>
                          <button className="px-4 py-2 bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white rounded-lg text-sm font-semibold hover:shadow-lg transition-all">
                            Generate Content
                          </button>
                        </div>
                      </div>

                      {/* Input Area */}
                      <div className="mb-6">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          What do you want to post about?
                        </label>
                        <textarea
                          className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-[#66b3ff] focus:outline-none resize-none"
                          rows={3}
                          placeholder="e.g., Announce our new product launch for eco-friendly water bottles..."
                          defaultValue="Announce our new product launch for eco-friendly water bottles"
                        />
                      </div>

                      {/* AI Controls */}
                      <div className="grid md:grid-cols-3 gap-4 mb-6">
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-2">
                            Tone
                          </label>
                          <select className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-[#66b3ff] focus:outline-none text-sm">
                            <option>Professional</option>
                            <option>Casual</option>
                            <option>Funny</option>
                            <option>Inspiring</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-2">
                            Length
                          </label>
                          <select className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-[#66b3ff] focus:outline-none text-sm">
                            <option>Short</option>
                            <option>Medium</option>
                            <option>Long</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-2">
                            Include
                          </label>
                          <select className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-[#66b3ff] focus:outline-none text-sm">
                            <option>Hashtags</option>
                            <option>Emojis</option>
                            <option>Call-to-Action</option>
                            <option>All</option>
                          </select>
                        </div>
                      </div>

                      {/* AI Generated Content Preview */}
                      <div className="bg-gradient-to-br from-[#e4f4ff]/20 to-[#b3d9ff]/20 rounded-xl p-6 border-2 border-[#66b3ff]/30">
                        <div className="flex items-center gap-2 mb-4">
                          <i className="ri-ai-generate text-[#22577A] text-xl animate-pulse"></i>
                          <span className="text-sm font-semibold text-[#22577A]">
                            AI Generated Content
                          </span>
                        </div>
                        <div className="bg-white rounded-lg p-4 mb-4">
                          <p className="text-gray-800 leading-relaxed">
                            🌊 Exciting news! We're thrilled to introduce our new line of eco-friendly water bottles!
                            Made from 100% recycled materials, these bottles help you stay hydrated while protecting
                            our planet. 🌍♻️
                            <br /><br />
                            ✨ Features:<br />
                            • Keeps drinks cold for 24h, hot for 12h<br />
                            • BPA-free & leak-proof design<br />
                            • Every purchase plants a tree 🌿<br />
                            <br />
                            Available now! Link in bio 👆
                            <br /><br />
                            #EcoFriendly #Sustainability #NewProduct #GoGreen #ZeroWaste
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button className="flex-1 px-4 py-2 bg-white border-2 border-gray-200 text-gray-700 rounded-lg text-sm font-semibold hover:border-[#66b3ff] transition-colors">
                            <i className="ri-refresh-line mr-2"></i>
                            Regenerate
                          </button>
                          <button className="flex-1 px-4 py-2 bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white rounded-lg text-sm font-semibold hover:shadow-lg transition-all">
                            <i className="ri-check-line mr-2"></i>
                            Use This Content
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Smart Analytics Dashboard */}
          {activeTab === 'analytics' && (
            <div className="animate-fadeIn">
              <div className="bg-white rounded-3xl shadow-2xl border-2 border-gray-200 p-8">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h3 className="text-2xl font-bold text-[#22577A] mb-2">Performance Dashboard</h3>
                    <p className="text-gray-600">AI-powered insights for smarter decisions</p>
                  </div>
                  <div className="flex gap-2">
                    <button className="px-4 py-2 border-2 border-gray-200 text-gray-700 rounded-lg text-sm font-semibold hover:border-[#66b3ff] transition-colors">
                      Last 7 Days
                    </button>
                    <button className="px-4 py-2 bg-[#22577A] text-white rounded-lg text-sm font-semibold">
                      Last 30 Days
                    </button>
                  </div>
                </div>

                {/* Stats Cards */}
                <div className="grid md:grid-cols-4 gap-6 mb-8">
                  {[
                    { label: 'Total Reach', value: '127.5K', change: '+12.3%', icon: 'ri-eye-line', color: 'from-[#22577A] to-[#38a3a5]' },
                    { label: 'Engagement Rate', value: '8.4%', change: '+2.1%', icon: 'ri-heart-line', color: 'from-[#38a3a5] to-[#66b3ff]' },
                    { label: 'New Followers', value: '3,247', change: '+18.5%', icon: 'ri-user-add-line', color: 'from-[#66b3ff] to-[#b3d9ff]' },
                    { label: 'Posts Published', value: '42', change: '+5', icon: 'ri-file-list-line', color: 'from-[#b3d9ff] to-[#e4f4ff]' },
                  ].map((stat, idx) => (
                    <div key={idx} className="bg-gradient-to-br from-[#e4f4ff]/10 to-[#b3d9ff]/10 rounded-xl p-6 border border-gray-200">
                      <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center mb-4`}>
                        <i className={`${stat.icon} text-white text-xl`}></i>
                      </div>
                      <div className="text-3xl font-bold text-[#22577A] mb-1">{stat.value}</div>
                      <div className="text-sm text-gray-600 mb-2">{stat.label}</div>
                      <div className="flex items-center gap-1 text-xs font-semibold text-[#66b3ff]">
                        <i className="ri-arrow-up-line"></i>
                        {stat.change}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Chart Area */}
                <div className="grid lg:grid-cols-3 gap-6">
                  {/* Engagement Chart */}
                  <div className="lg:col-span-2 bg-gradient-to-br from-[#e4f4ff]/10 to-[#b3d9ff]/10 rounded-xl p-6 border border-gray-200">
                    <h4 className="text-lg font-bold text-[#22577A] mb-4">Engagement Over Time</h4>
                    <div className="h-48 flex items-end justify-between gap-2">
                      {[65, 78, 45, 89, 72, 95, 88, 76, 92, 85, 98, 90].map((height, idx) => (
                        <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                          <div
                            className="w-full bg-gradient-to-t from-[#22577A] to-[#66b3ff] rounded-t-lg transition-all hover:opacity-80 cursor-pointer"
                            style={{ height: `${height}%` }}
                          ></div>
                          <span className="text-xs text-gray-500">{idx + 1}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Platform Breakdown */}
                  <div className="bg-gradient-to-br from-[#e4f4ff]/10 to-[#b3d9ff]/10 rounded-xl p-6 border border-gray-200">
                    <h4 className="text-lg font-bold text-[#22577A] mb-4">Top Platforms</h4>
                    <div className="space-y-4">
                      {[
                        { platform: 'Instagram', percent: 35, color: '#E4405F', icon: 'ri-instagram-line' },
                        { platform: 'Facebook', percent: 28, color: '#1877F2', icon: 'ri-facebook-fill' },
                        { platform: 'LinkedIn', percent: 22, color: '#0A66C2', icon: 'ri-linkedin-fill' },
                        { platform: 'Twitter', percent: 15, color: '#000000', icon: 'ri-twitter-x-line' },
                      ].map((platform, idx) => (
                        <div key={idx}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <i className={platform.icon} style={{ color: platform.color }}></i>
                              <span className="text-sm font-semibold text-gray-700">{platform.platform}</span>
                            </div>
                            <span className="text-sm font-bold text-[#22577A]">{platform.percent}%</span>
                          </div>
                          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${platform.percent}%`,
                                backgroundColor: platform.color,
                              }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Campaign Performance & Competitor Comparison */}
                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  {/* Campaign Tracking */}
                  <div className="bg-gradient-to-br from-[#e4f4ff]/30 to-[#b3d9ff]/30 rounded-xl p-6 border-2 border-[#66b3ff]/30">
                    <div className="flex items-center gap-2 mb-4">
                      <i className="ri-folder-chart-line text-[#22577A] text-xl"></i>
                      <h4 className="font-bold text-[#22577A]">Active Campaigns</h4>
                    </div>
                    <div className="space-y-3">
                      <div className="bg-white rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-gray-800 text-sm">Spring Launch</span>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">Active</span>
                        </div>
                        <div className="text-2xl font-bold text-[#22577A] mb-1">2.4M</div>
                        <div className="text-xs text-gray-600 mb-2">Total Reach</div>
                        <div className="flex items-center gap-2 text-xs text-green-600 font-semibold">
                          <i className="ri-arrow-up-line"></i>
                          +32% vs. last campaign
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-gray-800 text-sm">Summer Promo</span>
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">Scheduled</span>
                        </div>
                        <div className="text-sm text-gray-600">
                          Launches in 5 days
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Competitor Comparison */}
                  <div className="bg-gradient-to-br from-[#e4f4ff]/30 to-[#b3d9ff]/30 rounded-xl p-6 border-2 border-[#66b3ff]/30">
                    <div className="flex items-center gap-2 mb-4">
                      <i className="ri-trophy-line text-[#22577A] text-xl"></i>
                      <h4 className="font-bold text-[#22577A]">vs. Competitors</h4>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-semibold text-[#22577A]">Your Brand</span>
                          <span className="text-xs font-bold text-[#22577A]">8.7%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-gradient-to-r from-[#22577A] to-[#38a3a5] h-2 rounded-full" style={{width: '87%'}}></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-600">Competitor A</span>
                          <span className="text-xs text-gray-600">6.2%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-gray-400 h-2 rounded-full" style={{width: '62%'}}></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-600">Industry Avg</span>
                          <span className="text-xs text-gray-600">5.5%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-gray-300 h-2 rounded-full" style={{width: '55%'}}></div>
                        </div>
                      </div>
                      <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center gap-1 text-green-700 text-xs font-semibold">
                          <i className="ri-trophy-fill"></i>
                          Outperforming industry by 58%!
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* AI Insights */}
                <div className="mt-6 bg-gradient-to-r from-[#22577A] to-[#38a3a5] rounded-xl p-6 text-white">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-lightbulb-line text-2xl"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-lg mb-2">AI Insights</h4>
                      <p className="text-white/90 text-sm leading-relaxed">
                        Your Instagram Reels are performing 45% better than average. Consider posting more video content
                        on Tuesdays and Thursdays between 2-4 PM for maximum engagement.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Post Planning & Scheduling */}
          {activeTab === 'planning' && (
            <div className="animate-fadeIn">
              <div className="bg-white rounded-3xl shadow-2xl border-2 border-gray-200 overflow-hidden">
                <div className="grid lg:grid-cols-4 gap-0">
                  {/* Calendar Sidebar */}
                  <div className="lg:col-span-1 bg-[#f8f9fa] border-r border-gray-200 p-6">
                    <div className="mb-6">
                      <h3 className="text-sm font-bold text-gray-500 uppercase mb-4">November 2025</h3>
                      <div className="grid grid-cols-7 gap-1 text-center text-xs">
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, idx) => (
                          <div key={idx} className="text-gray-500 font-semibold py-2">{day}</div>
                        ))}
                        {Array.from({ length: 30 }, (_, i) => (
                          <div
                            key={i}
                            className={`py-2 rounded-lg cursor-pointer transition-all ${
                              i === 8
                                ? 'bg-gradient-to-br from-[#22577A] to-[#38a3a5] text-white font-bold'
                                : i === 10 || i === 15 || i === 20
                                ? 'bg-[#e4f4ff]/30 font-semibold text-[#22577A]'
                                : 'hover:bg-gray-200 text-gray-700'
                            }`}
                          >
                            {i + 1}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm">
                        <div className="w-3 h-3 bg-[#1877F2] rounded-full"></div>
                        <span className="text-gray-700">Facebook (8)</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <div className="w-3 h-3 bg-[#E4405F] rounded-full"></div>
                        <span className="text-gray-700">Instagram (12)</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <div className="w-3 h-3 bg-[#0A66C2] rounded-full"></div>
                        <span className="text-gray-700">LinkedIn (6)</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <div className="w-3 h-3 bg-[#000000] rounded-full"></div>
                        <span className="text-gray-700">Twitter (10)</span>
                      </div>
                    </div>
                  </div>

                  {/* Schedule View */}
                  <div className="lg:col-span-3 p-8">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-2xl font-bold text-[#22577A]">Content Schedule</h3>
                        <p className="text-gray-600">November 9, 2025 - 6 posts scheduled</p>
                      </div>
                      <button className="px-4 py-2 bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white rounded-lg font-semibold hover:shadow-lg transition-all">
                        <i className="ri-add-line mr-2"></i>
                        New Post
                      </button>
                    </div>

                    {/* Timeline */}
                    <div className="space-y-4">
                      {[
                        {
                          time: '9:00 AM',
                          platform: 'Instagram',
                          icon: 'ri-instagram-line',
                          color: '#E4405F',
                          content: 'Morning motivation post with quote',
                          status: 'scheduled',
                          image: true,
                        },
                        {
                          time: '12:30 PM',
                          platform: 'LinkedIn',
                          icon: 'ri-linkedin-fill',
                          color: '#0A66C2',
                          content: 'Industry insights article',
                          status: 'scheduled',
                          image: false,
                        },
                        {
                          time: '2:00 PM',
                          platform: 'Facebook',
                          icon: 'ri-facebook-fill',
                          color: '#1877F2',
                          content: 'Product announcement with video',
                          status: 'ai-optimizing',
                          image: true,
                        },
                        {
                          time: '4:30 PM',
                          platform: 'Twitter',
                          icon: 'ri-twitter-x-line',
                          color: '#000000',
                          content: 'Quick update thread (3 tweets)',
                          status: 'draft',
                          image: false,
                        },
                        {
                          time: '6:00 PM',
                          platform: 'Instagram',
                          icon: 'ri-instagram-line',
                          color: '#E4405F',
                          content: 'Reel: Behind the scenes footage',
                          status: 'scheduled',
                          image: true,
                        },
                        {
                          time: '8:00 PM',
                          platform: 'TikTok',
                          icon: 'ri-tiktok-line',
                          color: '#000000',
                          content: 'Tutorial video with trending audio',
                          status: 'scheduled',
                          image: true,
                        },
                      ].map((post, idx) => (
                        <div
                          key={idx}
                          className="flex items-start gap-4 p-4 bg-gradient-to-br from-[#e4f4ff]/10 to-[#b3d9ff]/10 rounded-xl border border-gray-200 hover:shadow-lg transition-all cursor-pointer"
                        >
                          <div className="flex-shrink-0">
                            <div
                              className="w-12 h-12 rounded-xl flex items-center justify-center"
                              style={{ backgroundColor: `${post.color}15` }}
                            >
                              <i className={`${post.icon} text-2xl`} style={{ color: post.color }}></i>
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="text-sm font-bold text-[#22577A]">{post.time}</span>
                              <span className="text-sm font-semibold text-gray-700">{post.platform}</span>
                              {post.status === 'ai-optimizing' && (
                                <span className="px-2 py-1 bg-gradient-to-r from-[#66b3ff] to-[#b3d9ff] text-white text-xs font-semibold rounded-full flex items-center gap-1">
                                  <i className="ri-ai-generate animate-pulse"></i>
                                  AI Optimizing
                                </span>
                              )}
                              {post.status === 'scheduled' && (
                                <span className="px-2 py-1 bg-[#e4f4ff]/30 text-[#22577A] text-xs font-semibold rounded-full">
                                  <i className="ri-check-line"></i> Scheduled
                                </span>
                              )}
                              {post.status === 'draft' && (
                                <span className="px-2 py-1 bg-gray-200 text-gray-600 text-xs font-semibold rounded-full">
                                  Draft
                                </span>
                              )}
                            </div>
                            <p className="text-gray-700 mb-2">{post.content}</p>
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                              {post.image && (
                                <span className="flex items-center gap-1">
                                  <i className="ri-image-line"></i> Media attached
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <i className="ri-time-line"></i> Auto-publish
                              </span>
                            </div>
                          </div>
                          <button className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-[#22577A] transition-colors">
                            <i className="ri-more-2-fill text-xl"></i>
                          </button>
                        </div>
                      ))}
                    </div>

                    {/* AI Suggestion */}
                    <div className="mt-6 bg-gradient-to-r from-[#22577A] to-[#38a3a5] rounded-xl p-4 text-white flex items-center gap-4">
                      <i className="ri-lightbulb-flash-line text-3xl"></i>
                      <div className="flex-1">
                        <p className="text-sm font-semibold mb-1">AI Suggestion</p>
                        <p className="text-xs text-white/90">
                          You have no posts scheduled for tomorrow. Your audience is most active at 10 AM and 3 PM.
                        </p>
                      </div>
                      <button className="px-4 py-2 bg-white text-[#22577A] rounded-lg text-sm font-semibold hover:shadow-lg transition-all">
                        Auto-Fill
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add animation CSS */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </section>
  );
}
